import { useEffect, useRef, useState } from 'react';
import { FileAudio, ImagePlus, LoaderCircle, Upload, X } from 'lucide-react';
import {
  useAvatarUploadUrl,
  useEnrollPersona,
  useStartTranscription,
  usePersonaEnrollmentStatus,
  useTranscriptionStatus,
  useVoiceSampleUploadUrl,
} from '@/hooks/useVoiceApi';

const PERSONA_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const DEFAULT_SYSTEM_PROMPT = `You are a digital twin of [PERSON NAME]. Think, decide, and communicate as the original person would, based on the detailed profile below. Speak in the first person and stay in character.

<twin_profile>
[ADD THE DETAILED PERSONA PROFILE HERE]
</twin_profile>

PHONE CALL MODE:
- This is a live phone or audio call, not a text chat.
- Greet the caller naturally at the start of the conversation.
- Keep responses concise, conversational, and easy to understand when spoken aloud. One to three sentences is the norm.
- Match the original person's documented tone, values, reasoning, decisions, and communication style.
- Do not use markdown, lists, headings, or text-only formatting.
- Ask a brief clarifying question when needed instead of giving a long written-style answer.`;

function createPersonaId(name: string) {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

async function uploadFile(ticketUrl: string, file: File) {
  const response = await fetch(ticketUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!response.ok) throw new Error('File upload failed');
}

export function PersonaEnrollmentDialog({ onClose, onReady }: { onClose: () => void; onReady: () => void }) {
  const uploadSampleTicket = useVoiceSampleUploadUrl();
  const uploadAvatarTicket = useAvatarUploadUrl();
  const startTranscription = useStartTranscription();
  const transcriptionStatus = useTranscriptionStatus();
  const enrollPersona = useEnrollPersona();
  const enrollmentStatus = usePersonaEnrollmentStatus();
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en');
  const [sample, setSample] = useState<File>();
  const [avatar, setAvatar] = useState<File>();
  const [sampleUploaded, setSampleUploaded] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const transcriptEdited = useRef(false);
  const [avatarPreview, setAvatarPreview] = useState<string>();

  useEffect(() => () => { if (avatarPreview) URL.revokeObjectURL(avatarPreview); }, [avatarPreview]);

  function handleAvatarChange(file: File | undefined) {
    setAvatar(file);
    setAvatarPreview(previous => {
      if (previous) URL.revokeObjectURL(previous);
      return file ? URL.createObjectURL(file) : undefined;
    });
  }

  function handleNameChange(value: string) {
    setName(value);
    setId(createPersonaId(value));
    setSampleUploaded(false);
  }

  async function ensureSampleUploaded() {
    if (!sample) throw new Error('Select a WAV voice sample');
    if (!PERSONA_ID_PATTERN.test(id)) throw new Error('Use a lowercase hyphenated persona ID');
    if (sampleUploaded) return;
    const ticket = await uploadSampleTicket.mutateAsync(id);
    await uploadFile(ticket.uploadUrl, sample);
    setSampleUploaded(true);
  }

  async function handleTranscribe() {
    setError('');
    setNotice('Uploading voice sample...');
    transcriptEdited.current = false;
    try {
      await ensureSampleUploaded();
      setNotice('Transcribing voice sample...');
      const job = await startTranscription.mutateAsync({ voiceId: id, language });
      for (let attempt = 0; attempt < 30; attempt += 1) {
        await new Promise(resolve => window.setTimeout(resolve, 2_000));
        const current = await transcriptionStatus.mutateAsync(job.jobId);
        if (current.status === 'COMPLETED') {
          if (!transcriptEdited.current && current.text) setTranscript(current.text);
          setNotice('Transcript ready. You can edit it before enrolling.');
          return;
        }
        if (current.status === 'FAILED') throw new Error(current.error ?? 'Transcription failed');
      }
      throw new Error('Transcription timed out. Enter the transcript manually.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Transcription failed');
      setNotice('');
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setNotice('Preparing enrollment...');
    try {
      if (!name || !transcript || !systemPrompt) throw new Error('Complete all required fields');
      await ensureSampleUploaded();
      let imageUrl: string | undefined;
      if (avatar) {
        const ticket = await uploadAvatarTicket.mutateAsync({ personaId: id, contentType: avatar.type });
        await uploadFile(ticket.uploadUrl, avatar);
        imageUrl = ticket.imageUrl;
      }
      await enrollPersona.mutateAsync({ id, name, language, transcript, systemPrompt, imageUrl });
      setNotice('Enrolling voice sample...');
      for (let attempt = 0; attempt < 60; attempt += 1) {
        await new Promise(resolve => window.setTimeout(resolve, 2_000));
        const persona = await enrollmentStatus.mutateAsync(id);
        if (persona.enrollmentStatus === 'ready' || persona.enrollmentStatus === undefined) {
          onReady();
          onClose();
          return;
        }
        if (persona.enrollmentStatus === 'failed') throw new Error('Voice sample could not be enrolled');
      }
      throw new Error('Voice enrollment is taking longer than expected. Check again shortly.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not start enrollment');
      setNotice('');
    }
  }

  const pending = uploadSampleTicket.isPending || uploadAvatarTicket.isPending || startTranscription.isPending || transcriptionStatus.isPending || enrollPersona.isPending || enrollmentStatus.isPending || notice === 'Enrolling voice sample...';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 px-4 py-8 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="enroll-persona-title">
      <form onSubmit={handleSubmit} className="mx-auto w-full max-w-xl rounded-xl border border-white/10 bg-zinc-900 p-6 shadow-2xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 id="enroll-persona-title" className="text-lg font-semibold text-white">Enroll a voice persona</h2>
            <p className="mt-1 text-sm text-white/50">Create a digital twin for phone conversations.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1 text-white/60 hover:bg-white/10 hover:text-white" aria-label="Close enrollment dialog"><X className="h-5 w-5" /></button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="grid gap-1.5 text-sm text-white/70">Display name<input required value={name} onChange={event => handleNameChange(event.target.value)} placeholder="Alex Meyer" className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/40" /></label>
          <label className="grid gap-1.5 text-sm text-white/70">Persona ID<input required readOnly value={id} placeholder="alex-meyer" className="rounded-lg border border-white/10 bg-black/50 px-3 py-2 text-white/60 outline-none" /></label>
          <label className="grid gap-1.5 text-sm text-white/70">Language<select value={language} onChange={event => setLanguage(event.target.value)} className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/40"><option value="en">English</option><option value="de">German</option></select></label>
          <label className="grid gap-1.5 text-sm text-white/70">Avatar image<input type="file" accept="image/png,image/jpeg,image/webp" onChange={event => handleAvatarChange(event.target.files?.[0])} className="sr-only" /><span className="flex h-10 items-center gap-2 rounded-lg border border-dashed border-white/20 px-3 text-xs text-white/50 hover:border-white/40"><ImagePlus className="h-4 w-4" />{avatar ? avatar.name : 'Choose image'}</span>{avatarPreview && <img src={avatarPreview} alt="Avatar preview" className="h-12 w-12 rounded-full object-cover" />}</label>
        </div>

        <label className="mt-4 grid gap-1.5 text-sm text-white/70">WAV voice sample<input required type="file" accept="audio/wav,.wav" onChange={event => { setSample(event.target.files?.[0]); setSampleUploaded(false); }} className="sr-only" /><span className="flex h-10 items-center gap-2 rounded-lg border border-dashed border-white/20 px-3 text-xs text-white/50 hover:border-white/40"><FileAudio className="h-4 w-4" />{sample ? sample.name : 'Choose WAV file'}</span></label>
        <button type="button" onClick={handleTranscribe} disabled={pending || !sample || !id} className="mt-2 flex items-center gap-2 text-sm text-white/70 hover:text-white disabled:opacity-40"><Upload className="h-4 w-4" />Transcribe sample</button>

        <label className="mt-4 grid gap-1.5 text-sm text-white/70">Transcript<textarea required value={transcript} onChange={event => { transcriptEdited.current = true; setTranscript(event.target.value); }} rows={4} className="resize-y rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/40" /></label>
        <div className="mt-4 flex items-center justify-between gap-3"><label className="grid flex-1 gap-1.5 text-sm text-white/70">System prompt<textarea required value={systemPrompt} onChange={event => setSystemPrompt(event.target.value)} rows={7} className="resize-y rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/40" /></label><button type="button" disabled title="Twin Agent API configuration is pending" className="self-start rounded-lg border border-white/10 px-3 py-2 text-xs text-white/35">Sync Twin</button></div>

        {error && <p className="mt-4 text-sm text-red-400">{error}</p>}
        {notice && <p className="mt-4 text-sm text-green-400">{notice}</p>}
        <div className="mt-6 flex justify-end gap-3"><button type="button" onClick={onClose} disabled={pending} className="rounded-lg px-4 py-2 text-sm text-white/70 hover:bg-white/10 disabled:opacity-50">Cancel</button><button disabled={pending} className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-black hover:bg-white/90 disabled:opacity-50">{pending && <LoaderCircle className="h-4 w-4 animate-spin" />}{notice === 'Enrolling voice sample...' ? 'Enrolling...' : 'Enroll persona'}</button></div>
      </form>
    </div>
  );
}